import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import BoltIconPurple from '../../assets/svg/bolt-purple.svg';
import { KeyCap, QuickLauncherHelpCard, QuickLauncherResultButton, ResultList, ShortcutSeparator } from './quick-launcher.shared';
import { getQuickLauncherDestinationKey } from './quick-launcher.util';
import { useQuickLauncherJump } from './use-quick-launcher-jump';

const Wrapper = styled.div`
  align-items: center;
  display: block;
  position: relative;
`;

const Field = styled.div`
  flex: 0 0 auto;
  position: relative;
  width: 270px;
`;

const Icon = styled.span`
  background: transparent url(${BoltIconPurple}) center center no-repeat;
  background-size: 13px;
  height: 14px;
  left: 12px;
  opacity: 0.72;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
`;

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  hasError: boolean;
  isExpanded: boolean;
}

const InputElement: React.FC<InputProps> = ({ hasError: _hasError, isExpanded: _isExpanded, ...inputProps }) => (
  <input {...inputProps} />
);

const Input = styled(InputElement)<InputProps>`
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid
    ${(props) => {
      if (props.hasError) {
        return props.theme.colors.negative;
      }

      return 'rgba(255, 255, 255, 0.38)';
    }};
  border-radius: 18px;
  box-shadow: ${(props) => {
    if (props.hasError) {
      return '0 0 0 2px rgba(255, 255, 255, 0.24)';
    }

    if (props.isExpanded) {
      return '0 0 0 2px rgba(255, 255, 255, 0.24)';
    }

    return 'none';
  }};
  box-sizing: border-box;
  color: ${(props) => props.theme.colors.body};
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 13px;
  height: 26px;
  letter-spacing: 0;
  outline: none;
  padding: 0 14px 0 34px;
  transition: border-color 0.18s, box-shadow 0.18s;
  width: 100%;

  ::placeholder {
    color: ${(props) => props.theme.colors.placeholder};
    font-family: ${(props) => props.theme.typography.face.secondary};
  }

  &:focus {
    border-color: #fff;
  }
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid rgba(58, 49, 94, 0.1);
  border-radius: 8px;
  box-shadow: 0 18px 48px rgba(25, 21, 43, 0.22), 0 2px 8px rgba(25, 21, 43, 0.12);
  box-sizing: border-box;
  left: 135px;
  padding: 12px;
  position: absolute;
  top: calc(100% + 8px);
  transform: translateX(-50%);
  width: 460px;
  z-index: 10001;
`;

const ErrorMessage = styled.div`
  color: ${(props) => props.theme.colors.negative};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 0.875em;
  letter-spacing: 0;
`;

const QuickJumpInput: React.FC = () => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    attemptJump,
    clearError,
    destinations,
    error,
    handleChange: handleValueChange,
    hasValue,
    jumpTo,
    value,
  } = useQuickLauncherJump();

  const collapse = useCallback(() => {
    setIsExpanded(false);
    clearError();
  }, [clearError]);

  const goToValue = () => {
    if (attemptJump()) {
      setIsExpanded(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    goToValue();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleValueChange(event);
    setIsExpanded(true);
  };

  const handleFocus = () => {
    setIsExpanded(true);
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const nextFocusTarget = event.relatedTarget as Node | null;

    if (wrapperRef.current && nextFocusTarget && !wrapperRef.current.contains(nextFocusTarget)) {
      collapse();
    }
  };

  useEffect(() => {
    if (!isExpanded) {
      return;
    }

    const handleMouseDown = (event: MouseEvent) => {
      if (wrapperRef.current && event.target instanceof Node && !wrapperRef.current.contains(event.target)) {
        collapse();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        collapse();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [collapse, isExpanded]);

  return (
    <Wrapper ref={wrapperRef} onBlur={handleBlur}>
      <Field>
        <Icon />
        <Input
          aria-label="Jump to Reposit, Claim, Org, User, or email"
          autoComplete="off"
          data-1p-ignore="true"
          hasError={!!error}
          isExpanded={isExpanded}
          onChange={handleChange}
          onClick={handleFocus}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          onMouseDown={handleFocus}
          placeholder={isExpanded ? 'Enter an ID or user email' : 'Jump to ID or user email'}
          title={error || 'Enter a Reposit, Claim, Org, or User ID, or an email, then press Enter'}
          value={value}
        />
      </Field>
      {isExpanded ? (
        <Panel>
          {destinations.length ? (
            <ResultList>
              {destinations.map((item) => (
                <QuickLauncherResultButton
                  key={getQuickLauncherDestinationKey(item)}
                  destination={item}
                  onClick={() => {
                    jumpTo(item);
                    setIsExpanded(false);
                  }}
                />
              ))}
            </ResultList>
          ) : null}

          {!destinations.length && !hasValue ? (
            <QuickLauncherHelpCard
              footer={
                <React.Fragment>
                  Next time try <KeyCap>Cmd</KeyCap>
                  <ShortcutSeparator>+</ShortcutSeparator>
                  <KeyCap>K</KeyCap> to go even quicker.
                </React.Fragment>
              }
            />
          ) : null}

          {error ? <ErrorMessage>{error}</ErrorMessage> : null}
        </Panel>
      ) : null}
    </Wrapper>
  );
};

export default QuickJumpInput;
